import VectorLayer from "ol/layer/Vector"
import VectorSource from "ol/source/Vector"
import { Fill, Stroke, Style, Icon, Text } from "ol/style"
import GeoJSON from "ol/format/GeoJSON"
import i18next from "./i18next"
import {
  map,
  japanSimpleMapLayer,
  japanDetailedMapLayer,
  japanEEWMapLayer,
  japanPrefMapLayer,
  clearReportLayers,
} from "./map"
import {
  setFooter,
  showTitle,
  hideFullscreenTip,
  hideTsunamiLegend,
  hideEEWLegend,
  hideInfoLegend,
  setInfoLegend,
} from "./map_ui"
import { cleanupWaves, stopWaveAnimation } from "./wave_animation"

export const INTENSITY_ORDER_ASC = ["1", "2", "3", "4", "5-", "5+", "6-", "6+", "7"]
export const INTENSITY_ORDER_DESC = ["7", "6+", "6-", "5+", "5-", "4", "3", "2", "1"]
export const TSUNAMI_LEVEL_ORDER = ["alert", "warning", "notice"]

export function intensityText(name, type) {
  if (type === "intensity") {
    return `${i18next.t("xml.report.intensity_text")}${i18next.t(`xml.report.intensity.${name}`)}`
  }
  return i18next.t(`xml.report.tsunami.${name}`)
}

// —— 样式(带缓存,避免重复创建) ——

const intensityStyleCache = new Map()

export function getIntensityStyle(getAssetUrl, intensity) {
  if (intensityStyleCache.has(intensity)) return intensityStyleCache.get(intensity)
  const style = new Style({
    // crossOrigin:图标可能来自 CDN(跨域),悬浮命中检测需要读取 canvas,必须以 CORS 方式加载
    image: new Icon({ scale: 0.05, src: getAssetUrl(`data/images/is_${intensity}.png`), crossOrigin: "anonymous" }),
  })
  intensityStyleCache.set(intensity, style)
  return style
}

export function getEpicenterStyle(getAssetUrl, label, strokeWidth = 2) {
  return new Style({
    zIndex: 1000, // 置于震度标记之上
    image: new Icon({
      scale: 0.1,
      src: getAssetUrl("data/images/epicenter.png"),
      crossOrigin: "anonymous",
    }),
    text: new Text({
      font: "13px sans-serif",
      offsetY: 25,
      text: label,
      placement: "line",
      fill: new Fill({ color: "#000" }),
      stroke: new Stroke({ color: "#fff", width: strokeWidth }),
    }),
  })
}

// —— GeoJSON 要素构建 ——

export function buildShindoFeaturesFromDict(dictByIntensity, coordDict) {
  const features = []
  for (const intensity of INTENSITY_ORDER_ASC) {
    const names = dictByIntensity?.[intensity]
    if (!names) continue
    for (const name of names) {
      const entry = coordDict[name]
      if (!entry?.coordinates) continue
      features.push({
        type: "Feature",
        geometry: { type: "Point", coordinates: entry.coordinates },
        properties: { intensity, name },
      })
    }
  }
  return features
}

export function createVectorLayerFromFeatures(features, styleMapOrFn, name) {
  const styleFn =
    typeof styleMapOrFn === "function" ? styleMapOrFn : (feature) => styleMapOrFn[feature.getProperties()["intensity"]]

  const layer = new VectorLayer({
    source: new VectorSource({
      features: new GeoJSON().readFeatures({ type: "FeatureCollection", features }, { featureProjection: "EPSG:3857" }),
    }),
    style: styleFn,
  })
  if (name) layer.set("name", name)
  return layer
}

export function fitToLayer(layer, maxZoom = 7.9) {
  if (!layer) return
  const source = layer.getSource()
  if (!source || source.getFeatures().length === 0) return
  map.getView().fit(source.getExtent(), { padding: [50, 50, 50, 50], maxZoom })
}

// —— 报告信息面板 HTML ——

// datasetByLevel: { 震度/津品牌级: [地名, ...] }
export function reportInfoWrite(datasetByLevel, reportInfo, type = "intensity") {
  let html = `<div class="accordion pt-2" id="accordionIntensity">`
  const keys = type === "intensity" ? INTENSITY_ORDER_DESC : TSUNAMI_LEVEL_ORDER

  for (const level of keys) {
    if (!datasetByLevel[level]) continue
    html += `
      <div class="accordion-item">
        <h2 class="accordion-header" id="heading${level}">
          <button class="accordion-button" type="button" data-bs-toggle="collapse" data-bs-target="#collapse${level}" aria-expanded="true" aria-controls="collapse${level}">
            ${intensityText(level, type)}
          </button>
        </h2>
        <div id="collapse${level}" class="accordion-collapse collapse show" aria-labelledby="heading${level}" data-bs-parent="#accordion${level}">
          <div class="accordion-body">`

    for (const name of datasetByLevel[level]) {
      html += `<nobr class="pe-1">${name}</nobr>`
    }

    html += `</div></div></div>`
  }

  html += `</div>`
  reportInfo.innerHTML += html
}

// —— 海啸区域闪烁控制:点亮2秒,熄灭0.5秒,循环 ——

let tsunamiBlinkStop = true
let tsunamiBlinkTimer = null

export function stopTsunamiBlink() {
  tsunamiBlinkStop = true
  if (tsunamiBlinkTimer) {
    clearTimeout(tsunamiBlinkTimer)
    tsunamiBlinkTimer = null
  }
}

export function startTsunamiBlink(layer) {
  stopTsunamiBlink()
  tsunamiBlinkStop = false

  const blinkOn = () => {
    if (tsunamiBlinkStop) return
    if (layer) layer.setVisible(true)
    tsunamiBlinkTimer = setTimeout(blinkOff, 2000)
  }
  const blinkOff = () => {
    if (tsunamiBlinkStop) return
    if (layer) layer.setVisible(false)
    tsunamiBlinkTimer = setTimeout(blinkOn, 500)
  }

  blinkOn()
}

// —— 切换报告时的地图复位与图层替换 ——

// 各报告类型使用的日本底图图层
const japanBaseLayers = [japanSimpleMapLayer, japanDetailedMapLayer, japanEEWMapLayer, japanPrefMapLayer]

// 切换到指定底图;已在地图上时保持不动,避免同类型报告(如连续 EEW)切换时闪烁。传 null 时全部收起。
export function showBaseLayer(layer) {
  const attached = map.getLayers().getArray()
  for (const base of japanBaseLayers) {
    if (base === layer) {
      if (!attached.includes(base)) map.addLayer(base)
    } else if (attached.includes(base)) {
      map.removeLayer(base)
    }
  }
}

// 原子化替换报告动态图层:移除旧图层与波动画残留后加入新图层,同一渲染帧内完成,不产生中间空帧。
// 各渲染方法在全部异步数据就绪后调用,切换期间地图保持上一个报告的显示。
export function applyReportLayers(layers = []) {
  clearReportLayers()
  cleanupWaves()
  for (const layer of layers) map.addLayer(layer)
}

export function resetMapForReport() {
  showTitle()
  hideFullscreenTip()
  hideTsunamiLegend()
  hideEEWLegend()
  hideInfoLegend()
  setInfoLegend("")

  stopTsunamiBlink()
  // 立即停止旧的波动画;图层不动,由 applyReportLayers 在新图层就绪后统一替换
  stopWaveAnimation()

  setFooter(`Generated by Shiny-Map / ©2026 Shiny Project / Data Source: 気象庁`)
}
